import React, { useMemo } from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import type { PaymentMethod } from '@/types/residents/payment';
import { PaymentDetailsSummary } from './shared';

const WarningIcon = SVG_COMPONENTS.WARNING;

interface ConfirmPaymentContentProps {
  paymentAmount: number;
  fee: number;
  totalAmount: number;
  paymentMethod: PaymentMethod | null;
  residentName?: string;
}

/**
 * Confirm Payment Content - Shows payment summary before confirmation
 */
export const ConfirmPaymentContent: React.FC<ConfirmPaymentContentProps> = ({
  paymentAmount,
  fee,
  totalAmount,
  paymentMethod,
  residentName,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const paymentMethodLabel = useMemo(() => {
    return getPaymentMethodShortLabel(paymentMethod);
  }, [paymentMethod]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Warning Icon */}
        <View style={styles.iconContainer}>
          {WarningIcon && (
            <WarningIcon width={42} height={42} color={MARKETPLACE_COLORS.textSecondary} />
          )}
        </View>

        {/* Confirmation Message */}
        <Text style={[styles.confirmMessage, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Please confirm your payment details.
        </Text>

        {/* Payment Details */}
        <PaymentDetailsSummary
          paymentAmount={paymentAmount}
          fee={fee}
          totalAmount={totalAmount}
          paymentMethodLabel={paymentMethodLabel}
          residentName={residentName}
          containerStyle={styles.detailsContainer}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 40,
  },
  iconContainer: {
    width: 42,
    height: 42,
    marginBottom: 16,
  },
  confirmMessage: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 24,
  },
  detailsContainer: {
    width: '100%',
    maxWidth: 304,
  },
});

export default ConfirmPaymentContent;
